import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  LinearProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import WifiIcon from "@mui/icons-material/Wifi";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import LockIcon from "@mui/icons-material/Lock";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HistoryIcon from "@mui/icons-material/History";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import {
  useLlmTemplatesQuery,
  useDeleteLlmTemplateMutation,
  useEditTemplateMutation,
  useGetPhoneNUmberQuery,
} from "../store/services/whatsappApi";
import { cellSX, layoutCSS } from "../component/common/commonStyle";

// ─────────────────────────────────────────────────────────────
// SAFE HELPERS
// ─────────────────────────────────────────────────────────────
const safeHas = (arr, fn) => Array.isArray(arr) && arr.some(fn);

// ─────────────────────────────────────────────────────────────
// TEMPLATE TYPE DETECTOR
// ─────────────────────────────────────────────────────────────
const getTemplateType = (template) => {
  const components = template?.components || [];
  if (components.some((c) => c.type === "CAROUSEL")) return "Carousel";
  const header = components.find((c) => c.type === "HEADER");
  if (header && (header.format === "IMAGE" || header.format === "VIDEO")) return "Rich Card";
  const hasOTP = components.some((c) => c.type === "BUTTONS" && c.buttons?.some((b) => b.type === "OTP"));
  if (hasOTP) return "Authentication";
  return "Text";
};

// ─────────────────────────────────────────────────────────────
// HEADER MEDIA — handles IMAGE, VIDEO, and fallback
// ─────────────────────────────────────────────────────────────
const HeaderMedia = ({ url, format = "IMAGE", height = 130 }) => {
  const [imgStatus, setImgStatus] = useState("loading");

  useEffect(() => {
    if (format === "IMAGE") setImgStatus("loading");
  }, [url, format]);

  if (!url) {
    return (
      <Box
        sx={{
          width: "100%",
          height,
          bgcolor: "#efefef",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
        }}
      >
        {format === "VIDEO" ? (
          <VideoLibraryIcon sx={{ color: "#bbb", fontSize: 30 }} />
        ) : (
          <ImageIcon sx={{ color: "#bbb", fontSize: 30 }} />
        )}
        <Typography sx={{ fontSize: "0.6rem", color: "#bbb" }}>
          No {format === "VIDEO" ? "video" : "image"}
        </Typography>
      </Box>
    );
  }

  if (format === "VIDEO") {
    return (
      <Box sx={{ position: "relative", width: "100%", height, bgcolor: "#000" }}>
        <Box
          component="video"
          src={url}
          controls
          preload="metadata"
          sx={{ width: "100%", height, objectFit: "contain", display: "block" }}
        >
          <Box
            sx={{
              width: "100%",
              height,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#1a1a1a",
            }}
          >
            <VideoLibraryIcon sx={{ color: "#aaa", fontSize: 32 }} />
            <Typography sx={{ fontSize: "0.6rem", color: "#aaa", mt: 0.5 }}>
              Video unavailable
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            px: 0.7,
            py: 0.2,
            bgcolor: "rgba(0,0,0,0.55)",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            gap: 0.4,
            pointerEvents: "none",
          }}
        >
          <VideoLibraryIcon sx={{ fontSize: 11, color: "#fff" }} />
          <Typography sx={{ fontSize: "0.55rem", color: "#fff", fontWeight: 700 }}>
            VIDEO
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height }}>
      {imgStatus !== "loaded" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.4s infinite",
            "@keyframes shimmer": {
              "0%": { backgroundPosition: "200% 0" },
              "100%": { backgroundPosition: "-200% 0" },
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {imgStatus === "error" && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <ImageIcon sx={{ color: "#ccc", fontSize: 26 }} />
              <Typography sx={{ fontSize: "0.58rem", color: "#ccc", mt: 0.3 }}>
                Image unavailable
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Box
        component="img"
        src={url}
        alt="header"
        onLoad={() => setImgStatus("loaded")}
        onError={() => setImgStatus("error")}
        sx={{
          width: "100%",
          height,
          objectFit: "cover",
          display: "block",
          opacity: imgStatus === "loaded" ? 1 : 0,
          transition: "opacity 0.3s",
        }}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// STATUS BADGE
// ─────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  APPROVED: { bg: "#d4edda", color: "#155724" },
  PENDING: { bg: "#fff3cd", color: "#856404" },
  REJECTED: { bg: "#f8d7da", color: "#721c24" },
  FAILED: { bg: "#f8d7da", color: "#721c24" },
  DRAFT: { bg: "#e2e3e5", color: "#383d41" },
};
const statusStyle = (s) =>
  STATUS_STYLES[String(s).toUpperCase()] ?? { bg: "#e2e3e5", color: "#383d41" };

// ─────────────────────────────────────────────────────────────
// OTP BUTTON DISPLAY HELPER
// ─────────────────────────────────────────────────────────────
const getOTPButtonLabel = (otpType) => {
  const labels = {
    COPY_CODE: "Copy Code",
    ONE_TAP: "One-tap verification",
    ZERO_TAP: "Auto-fill OTP",
  };
  return labels[otpType] || otpType || "Verify Code";
};

// ─────────────────────────────────────────────────────────────
// ITERATIONS DIALOG — clean layout, same look as the Edit Template popup
// ─────────────────────────────────────────────────────────────
const BRAND = "#0288d1";
const EDIT_BORDER = "#e2e8f0";

const GOOD_BADGE = { bg: "#d4edda", color: "#155724" };
const BAD_BADGE = { bg: "#f8d7da", color: "#721c24" };
const NEUTRAL_BADGE = { bg: "#e2e3e5", color: "#383d41" };

const Badge = ({ label, bg, color, dot = false }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex", alignItems: "center", gap: 0.6,
      px: 1, py: "2px", borderRadius: "12px",
      fontSize: "0.7rem", fontWeight: 600, whiteSpace: "nowrap",
      bgcolor: bg, color,
    }}
  >
    {dot && <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color }} />}
    {label}
  </Box>
);

// Small summary tile (label on top, big value, optional extra below)
const StatTile = ({ label, children }) => (
  <Box sx={{ p: 1.3, border: `1px solid ${EDIT_BORDER}`, borderRadius: "10px", bgcolor: "#f8fafc", minWidth: 0 }}>
    <Typography sx={{ fontSize: "0.68rem", color: "#64748b", mb: 0.6 }}>{label}</Typography>
    {children}
  </Box>
);

const statValueSx = { fontSize: "1rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 };

const IterationsDialog = ({ open, onClose, data }) => {
  const template = data?.template;
  const pool = data?.pool;
  const iterations = data?.iterations ?? [];
  const { bg, color } = statusStyle(template?.status);

  const maxIter = pool?.max_iterations || 10;
  const maxDrifts = pool?.max_drifts ?? 2;
  const usedIter = pool?.iterations_total ?? iterations.length;
  const progress = Math.min(100, (usedIter / maxIter) * 100);
  const isActive = String(pool?.state || "").toLowerCase() === "active";
  const poolStateLabel = pool?.state
    ? String(pool.state).charAt(0).toUpperCase() + String(pool.state).slice(1)
    : "—";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      {/* ── Header bar ── */}
      <Box sx={{ bgcolor: BRAND, color: "#fff", px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 1.2 }}>
        <HistoryIcon sx={{ fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, lineHeight: 1.2 }}>
            Template Iterations
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {template?.name}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2.5, bgcolor: "#fff" }}>
        <Stack spacing={2.5}>
          {/* ── Original template (one compact row) ── */}
          <Box
            sx={{
              display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5,
              px: 1.5, py: 1, borderRadius: "10px", border: `1px solid ${EDIT_BORDER}`, bgcolor: "#fff",
            }}
          >
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#0f172a" }}>
              Original template
            </Typography>
            <Badge label={template?.status || "—"} bg={bg} color={color} />
            <Typography sx={{ fontSize: "0.7rem", color: "#64748b", wordBreak: "break-all" }}>
              ID: <strong style={{ color: "#334155" }}>{template?.template_id || "—"}</strong>
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>
              Category: <strong style={{ color: "#334155" }}>{template?.category || "—"}</strong>
            </Typography>
          </Box>

          {/* ── Pool summary ── */}
          {pool && (
            <Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1.2 }}>
                <StatTile label="Pool status">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: isActive ? "#22c55e" : "#94a3b8" }} />
                    <Typography sx={statValueSx}>{poolStateLabel}</Typography>
                  </Box>
                </StatTile>

                <StatTile label="Iterations used">
                  <Typography sx={statValueSx}>
                    {usedIter} <span style={{ color: "#94a3b8", fontWeight: 500 }}>/ {maxIter}</span>
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ mt: 0.8, height: 5, borderRadius: 3, bgcolor: "#e2e8f0", "& .MuiLinearProgress-bar": { bgcolor: BRAND, borderRadius: 3 } }}
                  />
                </StatTile>

                <StatTile label="Drifts">
                  <Typography sx={statValueSx}>
                    {pool.drifts ?? 0} <span style={{ color: "#94a3b8", fontWeight: 500 }}>/ {maxDrifts}</span>
                  </Typography>
                </StatTile>

                <StatTile label="Can replenish">
                  <Typography sx={{ ...statValueSx, color: pool.can_replenish ? "#15803d" : "#b91c1c" }}>
                    {pool.can_replenish ? "Yes" : "No"}
                  </Typography>
                </StatTile>
              </Box>

              {(pool.reason || pool.message) && (
                <Box sx={{ mt: 1.2, p: 1.3, borderRadius: "10px", bgcolor: "#f0f9ff", border: "1px solid #bae6fd" }}>
                  {pool.reason && (
                    <Typography sx={{ fontSize: "0.74rem", color: "#0f4c75", lineHeight: 1.55 }}>
                      <strong>Reason:</strong> {pool.reason}
                    </Typography>
                  )}
                  {pool.message && (
                    <Typography sx={{ fontSize: "0.74rem", color: "#0f4c75", lineHeight: 1.55, mt: pool.reason ? 0.5 : 0 }}>
                      {pool.message}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* ── Iterations timeline ── */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "#64748b" }}>
                Iterations
              </Typography>
              <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8" }}>
                {iterations.length} total
              </Typography>
            </Box>

            {iterations.length ? (
              <Box>
                {iterations.map((it, idx) => {
                  const st = statusStyle(it.status);
                  const comps = it.components || it.template?.components || [];
                  const bodyComp = comps.find((c) => c.type === "BODY");
                  const buttonComp = comps.find((c) => c.type === "BUTTONS");
                  const isLast = idx === iterations.length - 1;

                  return (
                    <Box key={it.id ?? idx} sx={{ display: "flex", gap: 1.5 }}>
                      {/* Number + connector line */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                        <Box
                          sx={{
                            width: 28, height: 28, borderRadius: "50%",
                            bgcolor: BRAND, color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: "0.72rem", fontWeight: 700,
                          }}
                        >
                          {it.iteration_index ?? idx + 1}
                        </Box>
                        {!isLast && <Box sx={{ flex: 1, width: "2px", bgcolor: EDIT_BORDER, my: 0.5 }} />}
                      </Box>

                      {/* Card */}
                      <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 2 }}>
                        <Box sx={{ p: 1.5, border: `1px solid ${EDIT_BORDER}`, borderRadius: "10px", bgcolor: "#fff" }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {it.name || `Iteration ${it.iteration_index ?? idx + 1}`}
                            </Typography>
                            <Badge label={it.status || "—"} bg={st.bg} color={st.color} />
                          </Box>

                          <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", mt: 0.3, wordBreak: "break-all" }}>
                            {[
                              it.template_id && `ID: ${it.template_id}`,
                              it.language,
                              it.updated_at && dayjs(it.updated_at).isValid() && dayjs(it.updated_at).format("MMM D, YYYY HH:mm"),
                            ].filter(Boolean).join("   •   ")}
                          </Typography>

                          {bodyComp?.text ? (
                            <Box sx={{ mt: 1.2, p: 1.2, borderRadius: "8px", bgcolor: "#f8fafc", borderLeft: `3px solid ${BRAND}` }}>
                              <Typography sx={{ fontSize: "0.78rem", color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                                {bodyComp.text}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ mt: 1, fontSize: "0.72rem", color: "#94a3b8", fontStyle: "italic" }}>
                              No content available
                            </Typography>
                          )}

                          {buttonComp?.buttons?.length > 0 && (
                            <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mt: 1 }}>
                              {buttonComp.buttons.map((b, i) => (
                                <Box
                                  key={i}
                                  component="span"
                                  sx={{ px: 1, py: "2px", borderRadius: "6px", fontSize: "0.66rem", fontWeight: 600, bgcolor: "#e0f2fe", color: "#0277bd" }}
                                >
                                  {b.text}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 4, border: `1px dashed ${EDIT_BORDER}`, borderRadius: "10px" }}>
                <Typography sx={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 600 }}>
                  No iterations yet
                </Typography>
                <Typography sx={{ color: "#94a3b8", fontSize: "0.72rem", mt: 0.3 }}>
                  Iterations will show up here once they are generated.
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${EDIT_BORDER}`, bgcolor: "#fafbfc" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          sx={{ textTransform: "none", fontSize: "0.78rem", borderRadius: "8px", px: 2 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
function LlmWhatsappList() {
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [lastUpdateFilter, setLastUpdateFilter] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Iterations dialog state
  const [iterationsDialogOpen, setIterationsDialogOpen] = useState(false);
  const [iterationsData, setIterationsData] = useState(null);

  // Phone Number dropdown
  const [selectedPhoneId, setSelectedPhoneId] = useState("");
  const [waba_id, setWaba_id] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editFormData, setEditFormData] = useState({
    bodyText: "",
    footerText: "",
    headerText: "",
    buttons: [],
    category: "",
    language: "",
  });
  const [editTemplateMutation, { isLoading: isEditing }] = useEditTemplateMutation();

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) { toast.error("Access token not found"); return; }
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (!id) toast.error("Customer ID not found in token");
      else setCustomerId(id);
    } catch { toast.error("Invalid access token format"); }
  }, []);

  const { currentData: phoneData } = useGetPhoneNUmberQuery(
    customerId,
    { skip: !customerId }
  );

  useEffect(() => {
    if (!selectedPhoneId && phoneData?.phone_numbers?.length) {
      const first = phoneData.phone_numbers[0];
      setSelectedPhoneId(first.phone_number_id);
      setWaba_id(first.waba_id || "");
    }
  }, [phoneData, selectedPhoneId]);

  const handlePhoneChange = (e) => {
    const id = e.target.value;
    setSelectedPhoneId(id);
    const matched = phoneData?.phone_numbers?.find((p) => p.phone_number_id === id);
    setWaba_id(matched?.waba_id || "");
  };

  const {
    currentData: templateData = { templates: [] },
    isLoading,
    isError,
    refetch,
  } = useLlmTemplatesQuery({ waba_id: waba_id, customer_id: customerId }, { skip: !waba_id });

  const [deleteLlmTemplate, { isLoading: isDeleting }] = useDeleteLlmTemplateMutation();

  // Keep full groups (original + pool + iterations) so we can look them up later
  const allTemplateGroups = useMemo(
    () => (templateData?.templates ?? []).filter((t) => t.original),
    [templateData]
  );

  // Fast lookup: original.template_id → { original, pool, iterations }
  const templateGroupMap = useMemo(() => {
    const map = new Map();
    allTemplateGroups.forEach((g) => {
      if (g.original?.template_id) map.set(g.original.template_id, g);
    });
    return map;
  }, [allTemplateGroups]);

  // List view uses only the "original" template fields
  const originalTemplates = useMemo(
    () => allTemplateGroups.map((t) => t.original),
    [allTemplateGroups]
  );

  const uniqueCategories = useMemo(
    () => [...new Set(originalTemplates.map((t) => t.category).filter(Boolean))],
    [originalTemplates]
  );

  const uniqueStatuses = useMemo(
    () => [...new Set(originalTemplates.map((t) => t.status).filter(Boolean))],
    [originalTemplates]
  );

  const filteredTemplates = useMemo(() => {
    return originalTemplates.filter((t) => {
      const s = searchTerm.toLowerCase();
      const matchSearch =
        !s ||
        t.name?.toLowerCase().includes(s) ||
        t.template_id?.toLowerCase().includes(s) ||
        t.waba_id?.toLowerCase().includes(s);
      const matchCat = !categoryFilter || t.category === categoryFilter;
      const matchStatus = !statusFilter || t.status === statusFilter;
      let matchDate = true;
      if (lastUpdateFilter && t.created_at) {
        const days = Math.floor((Date.now() - new Date(t.created_at)) / 86400000);
        matchDate = lastUpdateFilter === "recent" ? days <= 7 : days > 30;
      }
      return matchSearch && matchCat && matchStatus && matchDate;
    });
  }, [originalTemplates, searchTerm, categoryFilter, statusFilter, lastUpdateFilter]);

  const sortedTemplates = useMemo(
    () =>
      [...filteredTemplates].sort((a, b) => {
        const dA = new Date(a.created_at || 0).getTime();
        const dB = new Date(b.created_at || 0).getTime();
        return lastUpdateFilter === "oldest" ? dA - dB : dB - dA;
      }),
    [filteredTemplates, lastUpdateFilter]
  );

  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedTemplates = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedTemplates.slice(startIndex, endIndex);
  }, [sortedTemplates, page, rowsPerPage]);

  useEffect(() => { setPage(0); }, [searchTerm, categoryFilter, statusFilter, lastUpdateFilter]);

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    try {
      await deleteLlmTemplate({
        customer_id: customerId,
        template_id: template.template_id,
      }).unwrap();
      toast.success("Template deleted successfully");
    } catch {
      toast.error("Failed to delete template");
    }
  };

  const handlePreviewClick = (item) => {
    setSelectedTemplate(item);
    setPreviewOpen(true);
  };

  // Three-dot menu — opens the iterations dialog for this template
  const handleIterationsClick = (item) => {
    const group = templateGroupMap.get(item.template_id);
    if (!group) {
      toast.info("No iteration data available for this template");
      return;
    }
    setIterationsData({
      template: group.original,
      pool: group.pool || null,
      iterations: Array.isArray(group.iterations) ? group.iterations : [],
    });
    setIterationsDialogOpen(true);
  };

  const handleOpenEdit = (template) => {
    setEditingTemplate(template);
    const comps = Array.isArray(template.components) ? template.components : [];
    setEditFormData({
      bodyText: comps.find((c) => c.type === "BODY")?.text || "",
      footerText: comps.find((c) => c.type === "FOOTER")?.text || "",
      headerText: comps.find((c) => c.type === "HEADER")?.text || "",
      buttons: [...(comps.find((c) => c.type === "BUTTONS")?.buttons || [])],
      category: template.category,
      language: template.language,
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      if (!editingTemplate || !customerId) return;
      const components = [];
      if (editFormData.bodyText) {
        const matches = editFormData.bodyText.match(/\{\{\d+\}\}/g) || [];
        const bc = { type: "BODY", text: editFormData.bodyText };
        if (matches.length) bc.example = { body_text: [matches.map(() => "sample")] };
        components.push(bc);
      }
      if (editFormData.footerText)
        components.push({ type: "FOOTER", text: editFormData.footerText });
      if (editFormData.headerText)
        components.push({ type: "HEADER", format: "TEXT", text: editFormData.headerText });
      if (editFormData.buttons?.length)
        components.push({
          type: "BUTTONS",
          buttons: editFormData.buttons.map((b) => ({ type: b.type || "QUICK_REPLY", text: b.text })),
        });
      const fd = new FormData();
      fd.append("components", JSON.stringify(components));
      fd.append("category", editFormData.category);
      fd.append("language", editFormData.language);
      await editTemplateMutation({
        customer_id: customerId,
        template_id: editingTemplate.template_id,
        formData: fd,
      }).unwrap();
      toast.success("Template updated");
      setEditDialogOpen(false);
      refetch();
    } catch (e) {
      toast.error(e?.data?.error?.message || "Failed to update template");
    }
  };

  // ── Carousel sub-component ──
  const CarouselPreviewGray = ({ cards }) => {
    const [activeIdx, setActiveIdx] = useState(0);
    const safeCards = Array.isArray(cards) ? cards : [];

    if (!safeCards.length) {
      return (
        <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
          <Typography sx={{ fontSize: "0.7rem", color: "#aaa" }}>No carousel cards</Typography>
        </Box>
      );
    }

    const currentCard = safeCards[activeIdx];
    const cardHeader = currentCard?.components?.find((c) => c.type === "HEADER");
    const cardBody = currentCard?.components?.find((c) => c.type === "BODY");
    const cardBtns = currentCard?.components?.find((c) => c.type === "BUTTONS");
    const cardMediaUrl = cardHeader?.example?.header_handle?.[0] ?? null;
    const cardMediaFormat = cardHeader?.format ?? "IMAGE";

    return (
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
          <IconButton size="small" disabled={activeIdx === 0} onClick={() => setActiveIdx((p) => p - 1)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          {safeCards.map((_, i) => (
            <Box
              key={i}
              onClick={() => setActiveIdx(i)}
              sx={{
                width: i === activeIdx ? 14 : 6,
                height: 6,
                borderRadius: "3px",
                bgcolor: i === activeIdx ? "#1976d2" : "#ccc",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            />
          ))}
          <IconButton
            size="small"
            disabled={activeIdx === safeCards.length - 1}
            onClick={() => setActiveIdx((p) => p + 1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ bgcolor: "#fff", borderRadius: "8px", overflow: "hidden" }}>
          {(cardHeader?.format === "IMAGE" || cardHeader?.format === "VIDEO") && (
            <HeaderMedia
              url={cardMediaUrl}
              format={cardMediaFormat}
              height={cardMediaFormat === "VIDEO" ? 140 : 110}
            />
          )}

          {cardHeader?.format === "TEXT" && cardHeader.text && (
            <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#000", p: 1 }}>
              {cardHeader.text}
            </Typography>
          )}

          {cardBody?.text && (
            <Typography sx={{ p: 1, fontSize: "0.75rem", color: "#333" }}>
              {cardBody.text}
            </Typography>
          )}

          {cardBtns?.buttons?.length > 0 && (
            <Box sx={{ px: 1, pb: 1 }}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {cardBtns.buttons.map((btn, bIdx) => (
                  <Tooltip
                    key={bIdx}
                    title={btn.type === "PHONE_NUMBER" && btn.phone_number ? btn.phone_number : ""}
                    arrow
                    disableHoverListener={btn.type !== "PHONE_NUMBER" || !btn.phone_number}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        fontSize: "0.65rem",
                        textTransform: "none",
                        borderRadius: "6px",
                        ...(bIdx === 0
                          ? { bgcolor: "#000", color: "#fff" }
                          : { bgcolor: "#f0f0f0", color: "#333" }),
                      }}
                    >
                      {btn.text}
                    </Button>
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          )}
        </Box>

        <Typography sx={{ textAlign: "center", fontSize: "0.6rem", color: "#aaa", mt: 0.3 }}>
          {activeIdx + 1} / {safeCards.length}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={layoutCSS}>
      {/* ── Top bar ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, mb: 2, p: 1 }}>
        <TextField
          placeholder="Search by name, ID, or business ID"
          size="small"
          sx={{ width: 250, "& .MuiInputBase-input": { fontSize: "0.75rem", py: "6px", px: "8px" } }}
          InputProps={{ startAdornment: <SearchIcon sx={{ color: "#1e88e5", mr: 0.5, fontSize: 20 }} /> }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Button
          variant="contained"
          size="small"
          sx={{ textTransform: "none", fontSize: 13, borderRadius: 1.5, px: 2 }}
          onClick={() => navigate("/Ai-template")}
        >
          Create Template
        </Button>
      </Box>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center",
        p: "1rem", borderRadius: "0.5rem",
        bgcolor: "#ffffff52", backdropFilter: "blur(10px)",
        boxShadow: "0 4px 12px rgba(14,165,233,0.1)", mb: 2,
      }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Filter by:</Typography>

        <FormControl size="small" sx={{ width: 180 }}>
          <InputLabel sx={{ fontSize: "0.75rem" }}>Phone Number</InputLabel>
          <Select
            label="Phone Number"
            value={selectedPhoneId}
            onChange={handlePhoneChange}
            sx={{ fontSize: "0.75rem" }}
          >
            {phoneData?.phone_numbers?.map((p) => (
              <MenuItem key={p.id} value={p.phone_number_id}>{p.phone_number}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {[
          { label: "Category", val: categoryFilter, set: setCategoryFilter, opts: uniqueCategories },
          { label: "Status", val: statusFilter, set: setStatusFilter, opts: uniqueStatuses },
        ].map(({ label, val, set, opts }) => (
          <FormControl key={label} size="small" sx={{ width: 130 }}>
            <InputLabel sx={{ fontSize: "0.75rem" }}>{label}</InputLabel>
            <Select label={label} value={val} onChange={(e) => set(e.target.value)} sx={{ fontSize: "0.75rem" }}>
              <MenuItem value="">All {label}s</MenuItem>
              {opts.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </Select>
          </FormControl>
        ))}
        <FormControl size="small" sx={{ width: 145 }}>
          <InputLabel sx={{ fontSize: "0.75rem" }}>Last Update</InputLabel>
          <Select
            label="Last Update"
            value={lastUpdateFilter}
            onChange={(e) => setLastUpdateFilter(e.target.value)}
            sx={{ fontSize: "0.75rem" }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="recent">Last 7 Days</MenuItem>
            <MenuItem value="oldest">Older than 30 Days</MenuItem>
          </Select>
        </FormControl>
        {(searchTerm || categoryFilter || statusFilter || lastUpdateFilter) && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSearchTerm("");
              setCategoryFilter("");
              setStatusFilter("");
              setLastUpdateFilter("");
            }}
          >
            Clear Filters
          </Button>
        )}
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          Showing {paginatedTemplates.length} of {filteredTemplates.length} templates
          {filteredTemplates.length !== originalTemplates.length &&
            ` (filtered from ${originalTemplates.length} total)`}
        </Typography>
      </Box>

      {/* ── Table ── */}
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead sx={{ "& th": { bgcolor: "#0288d1", color: "#fff", fontSize: "0.8rem", p: "6px", borderRight: "0.5px solid #1b1b1b" } }}>
            <TableRow>
              {["Template Name", "Category", "Phone Number", "Type", "Status", "Language", "Last Updated", "Actions"].map((h) => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!waba_id ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Select a phone number to view templates</TableCell>
              </TableRow>
            ) : isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Loading templates...</TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Error loading templates</TableCell>
              </TableRow>
            ) : paginatedTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {originalTemplates.length > 0
                    ? "No templates match your filters"
                    : "No templates found"}
                </TableCell>
              </TableRow>
            ) : (
              paginatedTemplates.map((item) => {
                const { bg, color } = statusStyle(item.status);
                const type = getTemplateType(item);
                const group = templateGroupMap.get(item.template_id);
                const iterationCount = group?.iterations?.length || 0;
                return (
                  <TableRow
                    key={item.template_id}
                    sx={{ "& td": { p: "8px", fontSize: "0.75rem", borderRight: "0.5px solid #1b1b1b", borderBottom: "0.5px solid #1b1b1b" } }}
                  >
                    <TableCell sx={cellSX}>{item.name || "N/A"}</TableCell>
                    <TableCell sx={cellSX}>{item.category || "N/A"}</TableCell>
                    <TableCell sx={cellSX}>{item.phone_number || "N/A"}</TableCell>
                    <TableCell sx={cellSX}>{type}</TableCell>
                    <TableCell sx={cellSX}>
                      <span style={{ padding: "2px 8px", borderRadius: 12, fontSize: "0.7rem", backgroundColor: bg, color }}>
                        {item.status || "N/A"}
                      </span>
                    </TableCell>
                    <TableCell sx={cellSX}>{item.language || "N/A"}</TableCell>
                    <TableCell sx={cellSX}>
                      {item.created_at ? (
                        <>
                          {new Date(item.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                          <br />
                          {new Date(item.created_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                        </>
                      ) : "N/A"}
                    </TableCell>
                    <TableCell sx={cellSX}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <PhoneAndroidIcon
                          sx={{ cursor: "pointer", color: "#1976d2", fontSize: "1.2rem" }}
                          onClick={() => handlePreviewClick(item)}
                        />
                        {/* Three-dot menu — opens iterations dialog */}
                        <Tooltip title={iterationCount ? `${iterationCount} iteration${iterationCount > 1 ? "s" : ""}` : "No iterations"}>
                          <MoreVertIcon
                            sx={{
                              cursor: "pointer",
                              color: iterationCount ? "#7b1fa2" : "#9e9e9e",
                              fontSize: "1.2rem",
                              position: "relative",
                            }}
                            onClick={() => handleIterationsClick(item)}
                          />
                        </Tooltip>
                        <Tooltip title="Delete template">
                          <span>
                            <IconButton
                              aria-label={`Delete template ${item.name || item.template_id}`}
                              size="small"
                              disabled={isDeleting}
                              onClick={() => handleDelete(item)}
                            >
                              <DeleteIcon sx={{ color: "red", fontSize: "1.2rem" }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {!isLoading && !isError && filteredTemplates.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredTemplates.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-toolbar": { fontSize: "0.75rem", minHeight: "48px" },
              "& .MuiTablePagination-selectLabel": { fontSize: "0.75rem" },
              "& .MuiTablePagination-displayedRows": { fontSize: "0.75rem" },
              "& .MuiTablePagination-select": { fontSize: "0.75rem" },
            }}
          />
        )}
      </TableContainer>

      {/* ═══════════════════════════════════════════════════════
          WHATSAPP PREVIEW DIALOG
      ═══════════════════════════════════════════════════════ */}
      <Dialog
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setSelectedTemplate(null); }}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": { bgcolor: "transparent", boxShadow: "none", p: 0, overflow: "visible" },
          "& .MuiBackdrop-root": { bgcolor: "rgba(0,0,0,0.65)" },
        }}
      >
        <DialogContent sx={{
          p: 3, display: "flex", justifyContent: "center",
          alignItems: "flex-start", pt: 5,
          bgcolor: "transparent", overflow: "visible",
        }}>
          <Box sx={{
            width: 300,
            display: "flex", flexDirection: "column",
            bgcolor: "#000", borderRadius: "36px",
            boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
            overflow: "hidden", border: "6px solid #222",
          }}>
            <Box sx={{ height: 28, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#075E54" }}>
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: "#fff" }}>{dayjs().format("HH:mm")}</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "#fff" }}>
                <WifiIcon sx={{ fontSize: 13 }} />
                <NetworkCellIcon sx={{ fontSize: 13 }} />
                <BatteryFullIcon sx={{ fontSize: 13 }} />
              </Stack>
            </Box>

            <Box sx={{ bgcolor: "#075E54", px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#128C7E", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff" }}>
                  {selectedTemplate?.name?.[0]?.toUpperCase() ?? "W"}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedTemplate?.name ?? "Template"}
                </Typography>
                <Typography sx={{ fontSize: "0.59rem", color: "rgba(255,255,255,0.65)" }}>
                  {selectedTemplate?.category} • {selectedTemplate?.language}
                </Typography>
              </Box>
              {selectedTemplate?.status && (() => {
                const { bg, color } = statusStyle(selectedTemplate.status);
                return (
                  <Box sx={{ px: 0.9, py: 0.2, borderRadius: "8px", bgcolor: bg, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: "0.57rem", fontWeight: 700, color }}>{selectedTemplate.status}</Typography>
                  </Box>
                );
              })()}
            </Box>

            <Box sx={{
              flexGrow: 1, overflowY: "auto",
              px: 1, pt: 1.5, pb: 2,
              minHeight: 360, maxHeight: "62vh",
              bgcolor: "#f5f5f5",
              "&::-webkit-scrollbar": { width: "3px" },
              "&::-webkit-scrollbar-thumb": { bgcolor: "#bbb", borderRadius: "2px" },
            }}>
              {selectedTemplate ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{
                      fontSize: "0.6rem", color: "#555",
                      bgcolor: "rgba(255,255,255,0.75)", px: 1.5, py: 0.3, borderRadius: "8px",
                      display: "inline-block",
                    }}>
                      {dayjs(selectedTemplate.created_at).isValid()
                        ? dayjs(selectedTemplate.created_at).format("MMM D, YYYY")
                        : "Today"}
                    </Typography>
                  </Box>

                  <Box sx={{ width: "100%" }}>
                    {(() => {
                      const components = selectedTemplate?.components || [];
                      const carouselComp = components.find((c) => c.type === "CAROUSEL");
                      const buttonComp = components.find((c) => c.type === "BUTTONS");
                      const hasOTP = buttonComp?.buttons?.some((b) => b.type === "OTP");

                      if (carouselComp) {
                        const bodies = components.filter((c) => c.type === "BODY");
                        return (
                          <>
                            <CarouselPreviewGray cards={carouselComp.cards || []} />
                            {bodies.map((bodyComp, idx) => (
                              <Box key={idx} sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                                <Typography sx={{ fontSize: "0.8rem", color: "#333" }}>
                                  {bodyComp.text}
                                </Typography>
                              </Box>
                            ))}
                          </>
                        );
                      }

                      if (hasOTP) {
                        const bodyComp = components.find((c) => c.type === "BODY");
                        const footerComp = components.find((c) => c.type === "FOOTER");
                        const headerComp = components.find((c) => c.type === "HEADER");

                        return (
                          <>
                            <Box sx={{
                              bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1,
                              display: "flex", alignItems: "center", gap: 1,
                              borderLeft: "4px solid #d32f2f"
                            }}>
                              <LockIcon sx={{ fontSize: 16, color: "#d32f2f", flexShrink: 0 }} />
                              <Box>
                                <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#d32f2f" }}>
                                  Security verification
                                </Typography>
                                <Typography sx={{ fontSize: "0.65rem", color: "#666", mt: 0.3 }}>
                                  This is an authentication template
                                </Typography>
                              </Box>
                            </Box>

                            {headerComp?.text && (
                              <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#000" }}>
                                  {headerComp.text}
                                </Typography>
                              </Box>
                            )}

                            <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                              {bodyComp?.text ? (
                                <Typography sx={{ fontSize: "0.8rem", color: "#333" }}>
                                  {bodyComp.text}
                                </Typography>
                              ) : bodyComp?.add_security_recommendation ? (
                                <Typography sx={{ fontSize: "0.8rem", color: "#333" }}>
                                  Your verification code is: <strong>@x!yz</strong>
                                  <br />
                                  <Typography sx={{ fontSize: "0.7rem", color: "#666", mt: 0.5 }}>
                                    Don't share this code with anyone. We'll never ask you for it.
                                  </Typography>
                                </Typography>
                              ) : (
                                <Typography sx={{ fontSize: "0.8rem", color: "#333" }}>
                                  Your verification code is: <strong>@x!yz</strong>
                                </Typography>
                              )}
                            </Box>

                            {footerComp?.code_expiration_minutes && (
                              <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                                <Typography sx={{ fontSize: "0.7rem", color: "#999" }}>
                                  Code expires in {footerComp.code_expiration_minutes} minute{footerComp.code_expiration_minutes > 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            )}

                            {buttonComp?.buttons?.length > 0 && (
                              <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                                <Stack spacing={1}>
                                  {buttonComp.buttons.map((btn, bIdx) => (
                                    <Tooltip
                                      key={bIdx}
                                      title={btn.type === "PHONE_NUMBER" && btn.phone_number ? btn.phone_number : ""}
                                      arrow
                                      disableHoverListener={btn.type !== "PHONE_NUMBER" || !btn.phone_number}
                                    >
                                      <Button
                                        variant="contained"
                                        fullWidth
                                        sx={{
                                          fontSize: "0.7rem",
                                          textTransform: "none",
                                          borderRadius: "6px",
                                          bgcolor: "#075E54",
                                          color: "#fff",
                                          py: 1,
                                          "&:hover": { bgcolor: "#128C7E" }
                                        }}
                                      >
                                        <LockIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                        {getOTPButtonLabel(btn.otp_type)}
                                      </Button>
                                    </Tooltip>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </>
                        );
                      }

                      return (
                        <>
                          {components.some((c) => c.type === "HEADER") && (
                            <Box sx={{ bgcolor: "#fff", borderRadius: "8px", overflow: "hidden", mb: 1 }}>
                              {components
                                .filter((c) => c.type === "HEADER")
                                .map((headerComp, idx) => {
                                  const mediaUrl = headerComp?.example?.header_handle?.[0] ?? null;
                                  const mediaFormat = headerComp.format ?? "IMAGE";

                                  return (
                                    <Box key={idx}>
                                      {mediaFormat === "IMAGE" && (
                                        <HeaderMedia url={mediaUrl} format="IMAGE" height={110} />
                                      )}
                                      {mediaFormat === "VIDEO" && (
                                        <HeaderMedia url={mediaUrl} format="VIDEO" height={140} />
                                      )}
                                      {mediaFormat === "DOCUMENT" && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, bgcolor: "#fafafa" }}>
                                          <Box sx={{
                                            width: 36, height: 36, borderRadius: "6px",
                                            bgcolor: "#e53935", display: "flex",
                                            alignItems: "center", justifyContent: "center", flexShrink: 0,
                                          }}>
                                            <Typography sx={{ fontSize: "0.55rem", fontWeight: 700, color: "#fff" }}>PDF</Typography>
                                          </Box>
                                          <Typography sx={{ fontSize: "0.72rem", color: "#333" }}>
                                            {mediaUrl ? "Document attached" : "No document"}
                                          </Typography>
                                        </Box>
                                      )}
                                      {headerComp.text && mediaFormat !== "IMAGE" && mediaFormat !== "VIDEO" && mediaFormat !== "DOCUMENT" && (
                                        <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#000", p: 1 }}>
                                          {headerComp.text}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                            </Box>
                          )}

                          <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                            {components.filter((c) => c.type === "BODY").map((bodyComp, idx) => (
                              <Typography key={idx} sx={{ fontSize: "0.8rem", color: "#333" }}>
                                {bodyComp.text}
                              </Typography>
                            ))}
                            {components.filter((c) => c.type === "FOOTER").map((footerComp, idx) => (
                              <Typography key={idx} sx={{ fontSize: "0.7rem", color: "#999", mt: 0.5 }}>
                                {footerComp.text}
                              </Typography>
                            ))}
                          </Box>

                          {components.some((c) => c.type === "BUTTONS") && (
                            <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: "#666", textTransform: "uppercase", fontSize: "0.65rem", mb: 0.5, display: "block" }}>
                                Suggested Actions
                              </Typography>
                              <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                                {components
                                  .filter((c) => c.type === "BUTTONS")
                                  .flatMap((b) => b.buttons)
                                  .map((btn, bIdx) => (
                                    <Tooltip
                                      key={bIdx}
                                      title={btn.type === "PHONE_NUMBER" && btn.phone_number ? btn.phone_number : ""}
                                      arrow
                                      disableHoverListener={btn.type !== "PHONE_NUMBER" || !btn.phone_number}
                                    >
                                      <Button
                                        variant="contained"
                                        size="small"
                                        sx={{
                                          fontSize: "0.65rem",
                                          textTransform: "none",
                                          borderRadius: "6px",
                                          ...(bIdx === 0
                                            ? { bgcolor: "#000", color: "#fff" }
                                            : { bgcolor: "#f0f0f0", color: "#333" }),
                                        }}
                                      >
                                        {btn.text}
                                      </Button>
                                    </Tooltip>
                                  ))}
                              </Stack>
                            </Box>
                          )}
                        </>
                      );
                    })()}

                    <Typography sx={{ fontSize: "0.57rem", color: "#888", textAlign: "right", mt: 0.4 }}>
                      {dayjs(selectedTemplate.created_at).isValid()
                        ? dayjs(selectedTemplate.created_at).format("HH:mm")
                        : dayjs().format("HH:mm")} ✓✓
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
                  <Typography sx={{ color: "#aaa", fontSize: "0.75rem" }}>No template selected</Typography>
                </Box>
              )}
            </Box>
          </Box>

          <IconButton
            onClick={() => { setPreviewOpen(false); setSelectedTemplate(null); }}
            sx={{ position: "absolute", top: 8, right: 8, color: "#fff", bgcolor: "rgba(0,0,0,0.45)", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogContent>
      </Dialog>

      {/* ── Iterations Dialog ── */}
      <IterationsDialog
        open={iterationsDialogOpen}
        onClose={() => { setIterationsDialogOpen(false); setIterationsData(null); }}
        data={iterationsData}
      />

      {/* ── Edit Dialog ── */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Edit Template</Typography>
            <IconButton onClick={() => setEditDialogOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <Stack spacing={3}>
            {safeHas(editingTemplate?.components, (c) => c.type === "HEADER") && (
              <TextField
                label="Header Text" fullWidth size="small"
                value={editFormData.headerText}
                onChange={(e) => setEditFormData({ ...editFormData, headerText: e.target.value })}
              />
            )}
            <TextField
              label="Body Text" fullWidth multiline rows={4} size="small"
              value={editFormData.bodyText}
              helperText="Use {{1}}, {{2}} for variables"
              onChange={(e) => setEditFormData({ ...editFormData, bodyText: e.target.value })}
            />
            {safeHas(editingTemplate?.components, (c) => c.type === "FOOTER") && (
              <TextField
                label="Footer Text" fullWidth size="small"
                value={editFormData.footerText}
                onChange={(e) => setEditFormData({ ...editFormData, footerText: e.target.value })}
              />
            )}
            {editFormData.buttons.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Buttons</Typography>
                <Stack spacing={1}>
                  {editFormData.buttons.map((btn, i) => (
                    <TextField
                      key={i} label={`Button ${i + 1}`} fullWidth size="small"
                      value={btn.text}
                      onChange={(e) => {
                        const nb = [...editFormData.buttons];
                        nb[i] = { ...nb[i], text: e.target.value };
                        setEditFormData({ ...editFormData, buttons: nb });
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
            <Button variant="contained" onClick={handleEditSubmit} disabled={isEditing} fullWidth>
              {isEditing ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}

export default LlmWhatsappList;